// reset-account.ts — "Reset encryption / start fresh" for a user who has LOST
// their master key (no backup, no other signed-in device, no passkey on this
// browser) and is therefore stuck in the needsKey / read-only state.
//
// This is the lost-key RECOVERY path: it wipes the undecryptable encryption
// state + the personal workspace's ciphertext, detaches from shared workspaces
// the user can no longer decrypt, then reloads so the boot-time fresh-account
// auto-provision path re-keys the account cleanly (same account/email, brand-new
// master key). It is DESTRUCTIVE and irreversible — the personal workspace's
// notes/attachments are permanently deleted (they're encrypted under the lost
// key, so unrecoverable anyway). It is gated behind a typed confirmation in the
// UI (settings/sections/encryption.ts).
//
// Lives in workspaces/ (not encryption/) on purpose: it orchestrates UPWARD into
// WorkspaceManager, and the layering ratchet forbids the bottom encryption layer
// from importing workspaces. It reaches storage + Supabase through the window
// globals (no static import → no layering edge) and encryption downward via
// direct imports.

import { KeyStorage } from '../encryption/key-storage.js';
import { PasskeyVault } from '../encryption/passkey-vault.js';
import { clearEncryptionKeys, getEncryptionUserId } from '../encryption/state.js';
import { WorkspaceManager } from './workspace-manager.js';

/**
 * Wipe the lost-key encryption state + personal data and reload to re-provision
 * a fresh master key on the SAME account.
 *
 * Ordering is deliberate: detach shared workspaces first (idempotent, safe to
 * retry), purge personal ciphertext, then delete the `user_encryption` gate row
 * — and only reload once that gate delete is CONFIRMED applied. If the gate
 * delete fails we throw WITHOUT reloading, leaving the user safely in the
 * unchanged needsKey state (no corruption, just no progress).
 *
 * @returns {Promise<void>} resolves immediately before the reload.
 */
export async function resetEncryptionAndStartFresh(): Promise<void> {
  const supabase = window._supabaseClient;
  const userId = getEncryptionUserId();
  if (!supabase || !userId) {
    throw new Error('Not signed in — cannot reset encryption.');
  }

  // 1. Detach from workspaces this device can no longer decrypt.
  //    • shared with OTHER members → LEAVE (remove_member drops our membership +
  //      our wrapped-key row; co-members keep their data).
  //    • sole-owned shared → DELETE (don't orphan an empty cloud workspace).
  //    The personal workspace (id === userId) is handled in step 2.
  let workspaces: any[] = [];
  try {
    workspaces = (await WorkspaceManager.listMyWorkspaces()) || [];
  } catch (e: any) {
    console.warn('[reset] listMyWorkspaces failed; skipping shared cleanup:', e?.message);
  }
  for (const w of workspaces) {
    if (w.is_personal || w.id === userId) continue;
    try {
      if (w.member_count === 1 && w.role === 'admin') {
        await WorkspaceManager.deleteWorkspace(w.id);
      } else {
        await WorkspaceManager.leaveWorkspace(w.id);
      }
    } catch (e: any) {
      console.warn(`[reset] could not detach from workspace ${w.id}:`, e?.message);
    }
  }

  // 2. Purge the PERSONAL workspace's ciphertext (personal workspace_id === userId).
  //    The notes/attachments DELETE policies are ROLE-scoped, so we MUST filter
  //    by workspace_id — an unfiltered delete would reach co-members' rows in
  //    any shared workspace where we're admin/editor. Storage blobs don't cascade
  //    from row deletes, so remove them explicitly first.
  try {
    const { data: atts } = await supabase
      .from('attachments')
      .select('storage_path')
      .eq('workspace_id', userId);
    const paths = (atts || []).map((a: any) => a.storage_path).filter(Boolean);
    if (paths.length) {
      await supabase.storage
        .from('attachments')
        .remove(paths)
        .catch(() => {});
    }
    await supabase.from('attachments').delete().eq('workspace_id', userId);
  } catch (e: any) {
    console.warn('[reset] attachment purge failed:', e?.message);
  }
  try {
    await supabase.from('notes').delete().eq('workspace_id', userId);
  } catch (e: any) {
    console.warn('[reset] note purge failed:', e?.message);
  }

  // 3. Drop the local synced copy (force-clear — the data is unrecoverable and
  //    we're re-provisioning). Reuses the data-loss-safe drain-then-clear helper
  //    (powersync-clear.ts), exposed as _syncHelpers.clearLocalCopy.
  try {
    await window._syncHelpers?.clearLocalCopy?.(true);
  } catch (e: any) {
    console.warn('[reset] clearLocalCopy failed:', e?.message);
  }

  // 4. Delete the GATE row: user_encryption. Once it's gone, the next boot's
  //    DevicePairing.hasEncryptionRow() returns false → the boot path
  //    auto-provisions a fresh master key + static keypair instead of showing
  //    needsKey. This is the ONE delete we must confirm applied.
  const { error: encErr } = await supabase.from('user_encryption').delete().eq('user_id', userId);
  if (encErr) {
    throw new Error(`Reset failed — could not clear the encryption record: ${encErr.message}`);
  }
  // Best-effort: clear the (now-stale) device registry rows for this user.
  await supabase
    .from('user_devices')
    .delete()
    .eq('user_id', userId)
    .catch(() => {});
  await supabase
    .from('device_pairing')
    .delete()
    .eq('user_id', userId)
    .catch(() => {});

  // 5. Wipe local key material — BOTH the plain IDB record AND the passkey wrap
  //    (the passkey-wrapped copy lives in a separate vault DB, so deleteMasterKey
  //    alone wouldn't remove it).
  try {
    await PasskeyVault.disable(`mk_${userId}`);
  } catch {
    /* no passkey enrolled on this device — fine */
  }
  await KeyStorage.deleteMasterKey(userId).catch(() => {});
  clearEncryptionKeys();

  // 6. Signal the next boot to FORCE a fresh provision. We just deleted the
  //    user_encryption row, but Supabase may route the next boot's
  //    hasEncryptionRow() read to a replica that hasn't caught up yet — which
  //    would wrongly show "existing account → needsKey" and strand the user on a
  //    second reload. The boot path (initEncryptionAndWrap) consumes this marker:
  //    it re-deletes the row on its OWN connection (defeating the lag) and
  //    auto-provisions, then clears the marker.
  localStorage.setItem('ariadne_reset_provision', userId);

  // 7. Reload → fresh-account auto-provision (generates + persists a new master
  //    key, publishes a new static keypair, registers this device, wraps the
  //    personal workspace under the new key). Clean slate, same account.
  window.location.reload();
}

/**
 * Permanently delete the signed-in user's ENTIRE account via the `delete-account`
 * edge function (service-role auth.admin.deleteUser, which cascades all their DB
 * rows). The function takes the identity from the JWT only and refuses if the
 * user solely-owns a shared workspace (would destroy co-members' data). On
 * success this clears all local state and reloads into a signed-out app.
 *
 * @returns {Promise<void>} resolves immediately before the reload; throws (with a
 *   user-facing message) if the server refuses or fails.
 */
export async function deleteAccount(): Promise<void> {
  const supabase = window._supabaseClient;
  if (!supabase) throw new Error('Not signed in — cannot delete account.');
  const { data: sess } = await supabase.auth.getSession();
  const jwt = sess?.session?.access_token;
  if (!jwt) throw new Error('No active session — please sign in again.');

  const base = window.POWERSYNC_CREDENTIALS?.supabaseUrl;
  if (!base) throw new Error('Sync is not configured.');

  const resp = await fetch(`${base}/functions/v1/delete-account`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
  });
  if (!resp.ok) {
    // The function returns a JSON { error, message } for the owns-shared guard
    // (409); fall back to the raw text for other failures.
    let msg = `Account deletion failed (${resp.status}).`;
    const text = await resp.text().catch(() => '');
    try {
      const body = JSON.parse(text);
      if (body?.message) msg = body.message;
      else if (text) msg = text;
    } catch {
      if (text) msg = text;
    }
    throw new Error(msg);
  }

  // Account gone server-side — scrub every local trace and reload signed-out.
  try {
    await window._syncHelpers?.clearLocalCopy?.(true);
  } catch {
    /* non-fatal */
  }
  await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
  try {
    localStorage.clear();
  } catch {
    /* non-fatal */
  }
  window.location.reload();
}
